import { RangeAddress } from 'shared/excel/range-address';
import { Flowstep, Operation } from 'shared/types/flowstep';
import { flowstepStorage } from 'src/stores';
import { OfficeJsWorkbookApi } from './workbook-api/office-js-workbook-api';

export interface RangeDetectionResult {
  targetRangeAddress: RangeAddress;
  targetWorksheetId: string;
  tableHeaders: string[];
  firstCellValue?: string;
}

export interface RangeDetectionOptions {
  cellAddress?: string;
  worksheetId?: string;
}

export async function detectRange(options?: RangeDetectionOptions): Promise<RangeDetectionResult | null> {
  return await Excel.run(async (context) => {
    const workbookApi = new OfficeJsWorkbookApi(context);
    let cellAddress: string;
    let worksheetId: string;

    if (options?.cellAddress && options?.worksheetId) {
      cellAddress = options.cellAddress;
      worksheetId = options.worksheetId;
    } else {
      const activeCell = await workbookApi.getActiveCellInfo();
      cellAddress = activeCell.address;
      worksheetId = activeCell.worksheetId;
    }

    const detectedRange = await workbookApi.detectRangeByCell(worksheetId, cellAddress);

    if (!detectedRange) {
      return null;
    }

    detectedRange.load({ address: true });
    await context.sync();

    const tableHeaders = await workbookApi.getColumnHeaders(detectedRange);

    return {
      targetRangeAddress: RangeAddress.fromString(detectedRange.address),
      targetWorksheetId: worksheetId,
      tableHeaders,
    };
  });
}

export interface FlowstepDetectionOptions {
  targetRangeAddress: RangeAddress;
  worksheetId: string;
  operation?: Operation;
}

export function detectFlowstep(options: FlowstepDetectionOptions): Flowstep | undefined {
  const { targetRangeAddress, worksheetId, operation } = options;

  const existingFlowsteps = flowstepStorage.findFlowsteps({
    operation,
    options: {
      worksheetId,
    },
  });

  for (const flowstep of existingFlowsteps) {
    if (flowstep.options.worksheetId !== worksheetId) {
      continue;
    }

    const targetCell = RangeAddress.fromString(flowstep.options.target);
    if (targetRangeAddress.getIntersection(targetCell)) {
      return flowstep;
    }
  }

  return undefined;
}
